import { useDownloadShipmentLabel } from "@/api/use-download-shipment-label";
import { Navigate, useNavigate } from "react-router";
import { Button, Card, CardBody } from "@heroui/react";
import {
  ArrowRight,
  Bell,
  Check,
  CreditCard,
  Download,
  Package,
  Plus,
  Truck,
} from "lucide-react";
import { CreateShell } from "./create-order-layout";
import { useCreateOrder } from "./create-order-context";
import { ShipmentStatus } from "@/api.types.v2";
import type { LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

function formatCurrency(cents?: number): string {
  if (cents === undefined) return "$0.00";
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(cents / 100);
}

function formatTime(d: Date, locale: string): string {
  return d.toLocaleTimeString(locale, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZoneName: "short",
  });
}

interface NextStepRowProps {
  icon: LucideIcon;
  title: string;
  body: string;
  action?: string;
  onAction?: () => void;
  noBorder?: boolean;
  disabled?: boolean;
}

function NextStepRow({
  icon: Icon,
  title,
  body,
  action,
  onAction,
  noBorder,
  disabled,
}: NextStepRowProps) {
  return (
    <div
      className={`flex gap-4 py-5 ${
        noBorder ? "" : "border-b border-white/15"
      }`}
    >
      <span className="w-10 h-10 rounded-lg shrink-0 bg-white/10 grid place-items-center text-white">
        <Icon className="h-4.5 w-4.5" />
      </span>
      <div className="flex-1">
        <div className="text-base font-semibold text-white">{title}</div>
        <div className="text-sm text-white/70 mt-1 leading-relaxed">{body}</div>
      </div>
      {action && (
        <button
          type="button"
          onClick={onAction}
          disabled={disabled}
          className="self-start text-sm font-semibold text-white hover:underline"
        >
          {action} →
        </button>
      )}
    </div>
  );
}

export default function CreateShipmentConfirmationScreen() {
  const { t, i18n } = useTranslation("app");
  const navigate = useNavigate();
  const downloadLabel = useDownloadShipmentLabel();
  const { createdOrder, createdShipments, reset, discardDraft } =
    useCreateOrder();

  // Render-time guard: nothing to confirm without a created order (e.g. a
  // direct visit or refresh after the in-memory result was cleared).
  if (!createdOrder) return <Navigate to="/new-shipment/details" replace />;

  const shipment = createdShipments[0];
  const isPendingPayment = shipment?.status === ShipmentStatus.PENDING_PAYMENT;
  const labelUrl = shipment?.packages?.find((p) => p.labelUrl)?.labelUrl;
  // The charged amount is the order's tax-inclusive total — NOT the shipment
  // sell amount (that's the pre-tax label price and understates the charge).
  const totalCents = createdOrder.totalCents ?? shipment?.sellAmountCents ?? 0;
  const subtotalCents = createdOrder.subtotalCents;
  const taxCents = createdOrder.taxCents;

  const hasPickupBooking = shipment?.pickupBookingId != null;
  // ⚠️ `isOwnedFleet` WAS DEAD, AND HAS BEEN FOR A LONG TIME.
  //
  // It read `shipment.carrier.accessType` through an `as` cast — but no order
  // endpoint has ever nested a `carrier` on its shipments, so the expression was
  // always `undefined`, and `=== "OWNED"` was always false. The cast is what hid
  // it: it promised the compiler a shape the payload never had.
  //
  // `accessType` is now DELETED server-side, so there is nothing left to revive.
  //
  // ⚠️ The gap it leaves is REAL and is NOT fixed here: nothing models WHERE a
  // customer drops a parcel off. "Drop off at an EZX dispatch centre" vs "drop off
  // at the carrier" used to be inferred from `accessType === OWNED && !pickup`.
  // That inference is gone, and `selfRated` is NOT a replacement — self-rated means
  // "EZX prices this carrier", which says nothing about who physically receives the
  // parcel. Any partner can self-price. Handling needs its own signal.
  //
  // Until it has one, every drop-off is described as a carrier drop-off — which is
  // exactly what the (dead) code already did.
  const isOwnedFleet = false;
  const wePickUp = hasPickupBooking;
  const customerDropsOff = !hasPickupBooking;
  const isCarrierLabelOnly = customerDropsOff && !isOwnedFleet;
  const confirmationLedeKey = wePickUp
    ? "createShipment.confirmationLedePickup"
    : isCarrierLabelOnly
      ? "createShipment.confirmationLedeCarrier"
      : "createShipment.confirmationLedeDropoff";

  const handleCreateAnother = () => {
    // Safe to clear `createdOrder` here only because the guard below redirects
    // to this exact route — see handleViewShipments for why order matters.
    reset();
    void navigate("/new-shipment/details");
  };

  const handleViewShipments = () => {
    // ⚠️ Do NOT call reset() here. react-router wraps its location update in
    // `startTransition`, so it is LOW priority, while a setState from reset()
    // is urgent. React flushes the urgent one first: this screen re-renders at
    // the OLD location with `createdOrder === null`, the guard below fires, and
    // its `replace` overwrites the pending /dashboard entry — the button
    // appeared to do nothing.
    //
    // Only the draft needs clearing, and discardDraft() doesn't touch
    // `createdOrder`. The created-order state dies on its own: CreateOrderProvider
    // is mounted by the /new-shipment layout route, so leaving unmounts it.
    discardDraft();
    void navigate("/dashboard");
  };

  return (
    <CreateShell
      step={4}
      title={t("createShipment.confirmationTitle")}
      italic={t("createShipment.confirmationItalic")}
      lede={t(confirmationLedeKey, {
        orderNumber: createdOrder.orderNumber,
      })}
      footer={
        <>
          <Button
            variant="light"
            startContent={<Plus className="h-4 w-4" />}
            className="hidden md:inline-flex"
            onPress={handleCreateAnother}
          >
            {t("createShipment.createAnother")}
          </Button>
          <div className="flex gap-2 w-full md:w-auto">
            {labelUrl && (
              <Button
                onPress={() => shipment && downloadLabel.mutate(shipment.id)}
                isLoading={downloadLabel.isPending}
                isDisabled={downloadLabel.isPending}
                variant="flat"
                fullWidth
                className="md:w-auto"
                startContent={<Download className="h-4 w-4" />}
              >
                {t("createShipment.downloadLabel")}
              </Button>
            )}
            <Button
              color="primary"
              fullWidth
              className="md:w-auto"
              startContent={<Package className="h-4 w-4" />}
              endContent={<ArrowRight className="h-4 w-4" />}
              onPress={handleViewShipments}
            >
              {t("createShipment.viewShipments")}
            </Button>
          </div>
        </>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-6">
        {/* Left · indigo block */}
        <div
          className="rounded-2xl text-white px-6 py-7 md:px-10 md:py-9 flex flex-col"
          style={{
            background:
              "linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--primary)) 100%)",
          }}
        >
          <div className="flex items-center gap-3 mb-5">
            <span className="w-9 h-9 rounded-full bg-white/20 grid place-items-center">
              <Check className="h-4.5 w-4.5 text-white" />
            </span>
            <span className="text-tiny text-white/70 font-semibold uppercase tracking-wider">
              {t("createShipment.orderConfirmed", {
                time: formatTime(
                  new Date(),
                  i18n.language === "zh" ? "zh-CN" : "en-CA"
                ),
              })}
            </span>
          </div>
          <h2 className="text-[30px] md:text-4xl font-bold leading-[1.05] tracking-tight text-white">
            {t("createShipment.confirmedHeadline", {
              orderNumber: createdOrder.orderNumber,
            })}
            <br />
            <span className="italic opacity-80">
              {t("createShipment.confirmedItalic")}
            </span>
          </h2>

          <div className="mt-8">
            {isPendingPayment && (
              <NextStepRow
                icon={CreditCard}
                title={t("createShipment.completePayment")}
                body={t("createShipment.completePaymentBody")}
                action={t("createShipment.payNow")}
                onAction={handleViewShipments}
              />
            )}

            {wePickUp && (
              <NextStepRow
                icon={Truck}
                title={t("createShipment.pickupWindowConfirmed")}
                body={t("createShipment.pickupWindowBody")}
                action={t("createShipment.reschedule")}
                onAction={handleViewShipments}
              />
            )}

            {(isCarrierLabelOnly || labelUrl) && (
              <NextStepRow
                icon={Package}
                title={t("createShipment.printOrSkipLabel")}
                body={t("createShipment.printOrSkipBody")}
                action={labelUrl ? t("createShipment.downloadPdf") : undefined}
                onAction={() => shipment && downloadLabel.mutate(shipment.id)}
                disabled={downloadLabel.isPending}
              />
            )}

            <NextStepRow
              icon={Bell}
              title={t("createShipment.shareTrackingTitle")}
              noBorder
              body={t("createShipment.shareTrackingBody")}
              action={t("createShipment.copyLink")}
              onAction={() => {
                if (shipment?.trackingNumber) {
                  void navigator.clipboard.writeText(
                    `${window.location.origin}/track/${shipment.trackingNumber}`
                  );
                }
              }}
            />
          </div>
        </div>

        {/* Right · tracking + receipt */}
        <div className="flex flex-col gap-4">
          {shipment?.trackingNumber && (
            <Card shadow="none" className="border border-default-200">
              <CardBody className="px-6 py-5">
                <div className="text-tiny font-semibold uppercase tracking-wider text-default-500">
                  {t("createShipment.trackingNumber")}
                </div>
                <div className="text-xl font-mono font-bold mt-1.5 break-all">
                  {shipment.trackingNumber}
                </div>
                <a
                  href={`/track/${shipment.trackingNumber}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 text-sm text-primary hover:underline inline-flex items-center gap-1"
                >
                  {t("createShipment.openTrackingPage")} →
                </a>
              </CardBody>
            </Card>
          )}

          <Card shadow="none" className="border border-default-200">
            <CardBody className="px-6 py-5 gap-3">
              <div className="text-tiny font-semibold uppercase tracking-wider text-default-500">
                {t("createShipment.receipt")}
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-sm text-default-600">
                  {t("createShipment.order")}
                </span>
                <span className="font-mono text-sm font-medium">
                  ORD-{createdOrder.orderNumber}
                </span>
              </div>
              {shipment?.shipmentNumber && (
                <div className="flex justify-between items-baseline">
                  <span className="text-sm text-default-600">
                    {t("createShipment.shipment")}
                  </span>
                  <span className="font-mono text-sm font-medium">
                    SHP-{shipment.shipmentNumber}
                  </span>
                </div>
              )}
              {subtotalCents != null && (
                <div className="flex justify-between items-baseline pt-2 border-t border-default-200">
                  <span className="text-sm text-default-600">
                    {t("createShipment.subtotal")}
                  </span>
                  <span className="font-mono text-sm">
                    {formatCurrency(subtotalCents)}
                  </span>
                </div>
              )}
              {taxCents != null && taxCents > 0 && (
                <div className="flex justify-between items-baseline">
                  <span className="text-sm text-default-600">
                    {t("createShipment.hst")}
                  </span>
                  <span className="font-mono text-sm">
                    {formatCurrency(taxCents)}
                  </span>
                </div>
              )}
              <div
                className={`flex justify-between items-baseline pt-2 ${
                  subtotalCents == null ? "border-t border-default-200" : ""
                }`}
              >
                <span className="text-sm font-semibold text-default-700">
                  {t("createShipment.total")}
                </span>
                <span className="text-lg font-bold font-mono">
                  {formatCurrency(totalCents)}
                </span>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </CreateShell>
  );
}
